import React from "react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";

// Assets
import HeroImage from "../assets/Doctor-Frame.png";
import Reception from "../assets/reception.jpg";
import MRI from "../assets/MRI.jpg";
import CTScan from "../assets/CT Scan.jpg";
import OT from "../assets/OT.jpg";
import Laundry from "../assets/Laundry.jpg";
import Robotic from "../assets/Robotic.jpg";

const techCards = [
  {
    title: "Advanced MRI Technology",
    description: "Most advanced silent Magnetic Resonance Imaging (MRI) Technology for precise diagnostics with reduced noise and patient comfort.",
    image: MRI,
  },
  {
    title: "Gemstone Spectral Imaging CT Scan",
    description: "State-of-the-art Gemstone Spectral Imaging Technology CT Scan - Best of its kind for detailed medical imaging.",
    image: CTScan,
  },
  {
    title: "Modular Operation Theaters",
    description: "Best in the Class Modular Operation Theaters designed for optimal sterility and efficiency during surgical procedures.",
    image: OT,
  },
  {
    title: "Modern Laundry Facilities",
    description: "State of the art Laundry ensuring hospital linens and garments are processed with highest hygiene standards.",
    image: Laundry,
  },
  {
    title: "Robotic Physiotherapy",
    description: "Advanced Physiotherapy with Robotic facilities for better rehabilitation outcomes and patient recovery.",
    image: Robotic,
  },
];

const Infrastructure = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section with Overlay Text */}
      <div className="relative">
        <div
          className="h-80 md:h-96 bg-cover bg-center"
          style={{ backgroundImage: `url(${HeroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-transparent" />
        </div>
        <div className="absolute top-1/2 transform -translate-y-1/2 left-6 md:left-16 text-white max-w-lg">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">World-Class Infrastructure</h1>
          <div className="w-20 h-1 bg-teal-400 mb-4"></div>
          <p className="text-white/90 hidden md:block">
            Providing exceptional healthcare with cutting-edge facilities and modern technology
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Excellence in Healthcare Infrastructure
            </h2>
            <p className="text-gray-600">
              Our hospital is equipped with state-of-the-art facilities designed to provide the highest quality of care. 
              From advanced diagnostic equipment to comfortable patient rooms, every aspect of our infrastructure is 
              focused on delivering exceptional healthcare experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Modern Architecture Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Image with Decorations */}
            <div className="md:w-1/2 relative">
              <div className="absolute w-16 h-16 bg-teal-500/20 rounded-full -left-4 -top-4 hidden md:block"></div>
              <div className="absolute w-24 h-24 bg-purple-500/20 rounded-full -right-4 -bottom-4 hidden md:block"></div>
              
              <img
                src={Reception}
                alt="Hospital Reception"
                loading="lazy"
                className="w-full h-72 md:h-96 object-cover rounded-lg shadow-md"
              />
            </div>
            
            {/* Text Content */}
            <div className="md:w-1/2">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                <span className="text-teal-600">Modern</span> Architecture
              </h2>
              <div className="w-16 h-1 bg-teal-500 mb-6"></div>
              <p className="text-gray-600 mb-4">
                Designed with patient comfort and efficient healthcare delivery in mind, our hospital boasts a contemporary 
                and purposeful architectural layout. Bright and spacious interiors create a welcoming atmosphere, while 
                efficient navigation ensures that patients and visitors can easily access the services they need.
              </p>
              <p className="text-gray-600">
                Our architectural design prioritizes natural light, proper ventilation, and accessibility for all patients. 
                Each area is thoughtfully planned to create a healing environment that reduces stress and promotes recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Cutting-edge <span className="text-teal-600">Technology</span>
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Equipped with the latest diagnostic technologies, our centers enable swift and accurate identification 
              of medical conditions. Our advanced equipment empowers healthcare professionals to make informed decisions 
              and tailor treatment plans to individual needs.
            </p>
          </div>

          {/* Technology Cards - Better Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {techCards.map((card, index) => (
              <div
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    loading="lazy"
                    className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-teal-700 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities Overview */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Complete <span className="text-teal-600">Healthcare</span> Facilities
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our comprehensive facilities ensure that patients receive complete care under one roof.
              From diagnostics to treatment and rehabilitation, we provide a seamless healthcare experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Patient Care Areas",
                items: ["Private Rooms", "ICU Complex", "Emergency Department", "Outpatient Clinics"]
              },
              {
                title: "Diagnostic Services",
                items: ["Imaging Center", "Laboratory Services", "Cardiology Diagnostics", "Neurology Suite"]
              },
              {
                title: "Treatment Facilities",
                items: ["Surgical Complex", "Dialysis Unit", "Radiation Therapy", "Chemotherapy Suite"]
              },
              {
                title: "Support Services",
                items: ["Pharmacy", "Rehabilitation Center", "Nutrition Services", "Blood Bank"]
              }
            ].map((section, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-teal-700 mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg className="w-5 h-5 text-teal-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Infrastructure;